export const MEASUREMENT_MODES = {
  ANGLE: "angle",
  LENGTH: "length",
  CONSTRUCT: "construct",
};

function isComprehensionStem(question) {
  return (
    question?.type === "grouped" &&
    (question?.passage_type === "comprehension" || (question?.question_text?.length ?? 0) > 400)
  );
}

/**
 * Question text is stored as HTML with &nbsp; between words ("Construct&nbsp;triangle&nbsp;ADC"), so
 * every phrase pattern below — all of which expect a plain space — silently missed. Auto-detection
 * therefore never fired on real questions: a tool appeared only where an admin had set
 * force_measurement_tool by hand. Strip the tags and decode the entities before matching.
 */
function plainText(html) {
  return String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;|&#160;|&#xa0;/gi, " ")
    .replace(/ /g, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;|&#34;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function isReadingPassageStem(question) {
  if (String(question?.passage_type || "").toLowerCase() === "comprehension") {
    return true;
  }
  const passagePlain = plainText(question?.passage_stem);
  // Flattened comprehension leaves keep the full passage in passage_stem — do not treat
  // incidental wording there as a measurement instruction for each sub-part.
  return passagePlain.length > 400;
}

function combinedQuestionText(question) {
  const subs = (question?.sub_questions || [])
    .map((s) => plainText(s?.question_text))
    .join(" ");
  if (isComprehensionStem(question)) {
    return subs.toLowerCase();
  }
  const main = plainText(question?.question_text || question?.group_title);
  const passageStem = !isReadingPassageStem(question)
    ? plainText(question?.passage_stem)
    : "";
  return `${main} ${passageStem} ${subs}`.toLowerCase();
}

export function getMeasurementMode(question) {
  if (!question) return null;

  const forced = String(
    question?.answer_data?.force_measurement_tool ||
    question?.answer_data?.interactive?.force_tool ||
    question?.answer?.solution_geometry?.interactive?.force_tool ||
    ""
  ).toLowerCase();
  if (forced === "none") return null;
  if (forced === MEASUREMENT_MODES.ANGLE || forced === MEASUREMENT_MODES.LENGTH || forced === MEASUREMENT_MODES.CONSTRUCT) {
    return forced;
  }

  const combined = combinedQuestionText(question);

  // "The diagram is NOT drawn to scale" is the opposite of a measuring question: the figure is
  // deliberately misleading and the answer must be worked out. Handing over a protractor invites the
  // student to measure a diagram that lies.
  // "It is impossible to construct a triangle with sides 2 cm, 3 cm and 7 cm - explain why" is a
  // reasoning question ABOUT a construction that cannot be done.
  if (/\b(?:impossible|cannot|can't|unable|not possible)\b[^.?]{0,30}\bconstruct\b/.test(combined)) {
    return null;
  }
  if (/\bnot\b(?:\s+\S+){0,3}?\s+(?:drawn\s+)?to\s+scale\b/.test(combined)
    || /\bnot\s+drawn\s+accurately\b/.test(combined)
    || /\bnot\s+accurate(?:ly)?\b/.test(combined)) {
    return null;
  }
  // "a triangle with angles measuring 50°", "a carpet measuring 4m by 3m" — the participle STATES a
  // given, it does not ask anyone to measure anything.
  const measuringIsAGiven = /\b(?:angles?|sides?|lengths?|widths?|carpet|room|rectangle|square|triangle|garden|field|pool|sheet|piece)\s+measuring\b/.test(combined)
    || /\bmeasuring\s+\d/.test(combined);
  // A question can hand out an instrument or explicitly take it away. "Do not use a protractor"
  // contains the very phrase that otherwise proves an instrument is in play, so the ban is tested
  // first and suppresses the whole measure branch.
  const forbidsInstrument = /\b(?:do not|don't|cannot|can't|without|never|no)\b[^.?]{0,30}\b(?:use|using)?\s*(?:a |an |your |the |pair of )?(?:protractor|ruler|compasses|set square)\b/.test(combined)
    || /\bnot\s+allowed\s+to\s+measure\b/.test(combined);

  // Diagram already includes the printed instrument (Reading scales) — student reads it;
  // do not overlay a second virtual protractor/ruler unless admin force-tool.
  const diagramAlreadyHasInstrument =
    /\b(?:a|the)\s+(?:ruler|protractor|scale|thermometer|dial)\s+(?:is\s+)?(?:used|shown|given|drawn|provided)\b/.test(combined)
    || /\b(?:look at|shown (?:on|in)|from|on)\s+(?:the\s+)?(?:image|diagram|picture|ruler|protractor|scale)\b/.test(combined)
    || /\bwhat is the (?:length|reading|measurement)\b/.test(combined);

  // Noun "measure of angle/length" = size (Reading scales / angle knowledge), not imperative.
  const measureIsNounSize =
    /\bmeasure of (?:the\s+)?(?:angle|∠|line|length|side|arc)\b/.test(combined)
    || /\bwhat is the measure of\b/.test(combined);

  // Descriptive "is used to measure" (caption under a printed ruler) ≠ "use a ruler to measure".
  const measureIsDescriptiveCaption =
    /\b(?:is|was|are|were)\s+used\s+to\s+measure\b/.test(combined);

  // Imperative "measure" / named instrument only — NOT "what is the measure of angle…"
  // (noun "measure" = size). visual_type=angle_diagram alone must not hand out tools.
  // Allow "(c) Measure…" / "c) Measure…" part labels before the verb.
  const askedToMeasure = !measuringIsAGiven && !forbidsInstrument && !measureIsNounSize
    && !measureIsDescriptiveCaption && !diagramAlreadyHasInstrument && (
    /(?:^|[.;:!?]\s*|\([^)]{0,8}\)\s*|[a-z0-9]{1,3}\)\s*|\b(?:to|and|then|now|please|carefully)\s+)measure\b/.test(combined)
    || /\buse\s+(?:a|your|the)\s+(?:ruler|protractor|compasses|set square)/.test(combined)
    || /\bwith\s+(?:a|your|the)\s+(?:ruler|protractor|compasses|set square)/.test(combined)
    || /\bto the nearest degree\b/.test(combined)
  );

  if (
    /\bdraw accurately\b/.test(combined) ||
    /\baccurately\b.*\b(draw|construct)\b/.test(combined) ||
    /\b(draw|construct)\b.*\baccurately\b/.test(combined) ||
    /\bconstruct (a |the )?(triangle|rectangle|square|quadrilateral|polygon|parallelogram|trapezium)\b/.test(combined) ||
    // "draw the rectangle and write down the coordinates of corner D" is grid plotting, not a
    // construction — only count it when the question also hands over an instrument or a measurement.
    (/\bdraw (a |the )?(triangle|rectangle|square|quadrilateral|polygon|parallelogram|trapezium)\b/.test(combined)
      && askedToMeasure)
  ) {
    return MEASUREMENT_MODES.CONSTRUCT;
  }

  if (
    (!forbidsInstrument && !diagramAlreadyHasInstrument
      && (/\b(?:use|using|with)\s+(?:a|your|the)\s+protractor\b/.test(combined)
        || (askedToMeasure && /\bprotractor\b/.test(combined)))) ||
    // Every form below needs an actual instruction to measure. "Work out the size of angle x",
    // "find angle k", "what is the measure of angle DOA", and angle_diagram visuals alone are
    // calculated from labelled values — not measured with on-screen tools.
    (askedToMeasure && /\bangle/.test(combined))
  ) {
    return MEASUREMENT_MODES.ANGLE;
  }

  if (
    askedToMeasure && (
    /\bmeasure the line\b/.test(combined) ||
    /\bmeasure\s+(?:the\s+)?(?:length\s+of\s+)?(?:the\s+)?(?:line|segment)\b/.test(combined) ||
    // "Measure and write down the length of BD" — the verb and its object are separated by the rest of
    // the instruction, so the tight "measure the length" forms above all miss it and the part fell back
    // to whatever tool its siblings implied.
    /\bmeasure\b[^.?]{0,40}\blength\b/.test(combined) ||
    /\blength of (ab|line|side|segment)\b/.test(combined) ||
    /\bmeasure\s+(?:the\s+)?(?:length\s+of\s+)?(?:the\s+)?side\b/.test(combined) ||
    /\bmeasure (ab|the line ab)\b/.test(combined))
  ) {
    return MEASUREMENT_MODES.LENGTH;
  }

  if (
    !diagramAlreadyHasInstrument && askedToMeasure && (
    /\bestimate how far\b/.test(combined) ||
    /\bdrawn to a scale\b/.test(combined) ||
    /\bscale of\b/.test(combined) ||
    /\b1\s*cm\s*:\s*\d+/.test(combined) ||
    /\b1\s*cm\s+to\s+\d+/.test(combined) ||
    (/\bmap of\b/.test(combined) && /\b(scale|metres?|meters?|km|kilomet)/.test(combined)) ||
    (/\bfollowing the path\b/.test(combined) && /\b(metres?|meters?|km)\b/.test(combined)))
  ) {
    return MEASUREMENT_MODES.LENGTH;
  }

  // "Find the circumference of a circle with a radius of 14 cm (use π = 3.14)"
  // states the radius for a calculation. It is not an instruction to construct.
  const circleFormulaCalculation =
    /\b(?:find|calculate|work out|what is|write down)\b[^.?]{0,80}\b(?:circumference|area|diameter|perimeter)\b/.test(combined)
    && /\b(?:radius|diameter)\b/.test(combined)
    && !/\b(?:draw|construct|sketch|compasses|bisect)\b/.test(combined);
  if (circleFormulaCalculation) {
    return null;
  }

  if (
    /\b(?:mark|plot|draw|construct)\b[^.?]{0,40}\b\d+(?:\.\d+)?\s*cm from\b/.test(combined) ||
    /\bfraction of (ab|line)\b/.test(combined) ||
    /\bcompasses\b/.test(combined) ||
    /\bdraw (a |the )?circle\b/.test(combined) ||
    /\bdraw (a |the )?arc\b/.test(combined) ||
    /\bset square\b/.test(combined) ||
    /\bdraw a perpendicular\b/.test(combined) ||
    /\bbisect (the )?(line|angle)\b/.test(combined)
  ) {
    return MEASUREMENT_MODES.CONSTRUCT;
  }

  return null;
}

// Human-readable trigger phrases per tool, shown in the editor so an admin can see WHY "auto" resolved
// to a tool for a given question. Kept in sync with the regexes in getMeasurementMode above.
export const MEASUREMENT_AUTO_PHRASES = [
  { mode: MEASUREMENT_MODES.CONSTRUCT, test: (t) => /\bdraw accurately\b/.test(t), phrase: "draw accurately" },
  { mode: MEASUREMENT_MODES.CONSTRUCT, test: (t) => /\baccurately\b.*\b(draw|construct)\b/.test(t) || /\b(draw|construct)\b.*\baccurately\b/.test(t), phrase: "accurately draw / construct" },
  { mode: MEASUREMENT_MODES.CONSTRUCT, test: (t) => /\b(draw|construct) (a |the )?(triangle|rectangle|square|quadrilateral|polygon|parallelogram|trapezium)\b/.test(t), phrase: "draw / construct a shape" },
  { mode: MEASUREMENT_MODES.ANGLE, test: (t) => /\b(?:use|using|with)\s+(?:a|your|the)\s+protractor\b/.test(t) || (/\bprotractor\b/.test(t) && /\bmeasure\b/.test(t) && !/\bmeasure of\b/.test(t)), phrase: "use / measure with a protractor" },
  { mode: MEASUREMENT_MODES.ANGLE, test: (t) => /\bmeasure the angle/.test(t) || /\bmeasure.*\bangle/.test(t) || /\baccurately measure the angles/.test(t), phrase: "measure the angle" },
  { mode: MEASUREMENT_MODES.ANGLE, test: (t) => /\b(reflex|obtuse|acute)\b.*\bangle\b/.test(t) || /\bangle\b.*\b(reflex|obtuse|acute)\b/.test(t), phrase: "reflex / obtuse / acute angle" },
  { mode: MEASUREMENT_MODES.ANGLE, test: (t) => /\bangle between\b.*\b(hands?|lines?|arms?)\b/.test(t), phrase: "angle between the hands / lines" },
  { mode: MEASUREMENT_MODES.ANGLE, test: (t) => /\bsize of (the )?angle\b/.test(t), phrase: "size of the angle" },
  { mode: MEASUREMENT_MODES.LENGTH, test: (t) => /\bmeasure\b[^.?]{0,40}\blength\b/.test(t) || /\bmeasure the line\b/.test(t) || /\bmeasure\s+(?:the\s+)?(?:length\s+of\s+)?(?:the\s+)?(?:line|segment)\b/.test(t) || /\blength of (ab|line|side|segment)\b/.test(t) || /\bmeasure\s+(?:the\s+)?(?:length\s+of\s+)?(?:the\s+)?side\b/.test(t) || /\bmeasure (ab|the line ab)\b/.test(t), phrase: "measure the line / length" },
  { mode: MEASUREMENT_MODES.LENGTH, test: (t) => /\bscale of\b/.test(t) || /\bdrawn to a scale\b/.test(t) || /\b1\s*cm\s*:\s*\d+/.test(t) || /\bestimate how far\b/.test(t), phrase: "scale drawing" },
  { mode: MEASUREMENT_MODES.CONSTRUCT, test: (t) => /\bcompasses\b/.test(t) || /\bdraw (a |the )?circle\b/.test(t) || /\bdraw (a |the )?arc\b/.test(t) || (/\bwith (a )?radius( of)?\b/.test(t) && /\b(?:draw|construct|sketch)\b/.test(t)), phrase: "compasses / draw a circle or arc" },
  { mode: MEASUREMENT_MODES.CONSTRUCT, test: (t) => /\bset square\b/.test(t) || /\bdraw a perpendicular\b/.test(t) || /\bbisect (the )?(line|angle)\b/.test(t), phrase: "set square / perpendicular / bisect" },
];

// Returns { mode, phrase } describing what auto-detection would pick for this question's text, or null.
// Used by the editor to explain the "Auto" measurement-tool setting. Mode aligns with getMeasurementMode.
export function describeMeasurementAutoDetect(question) {
  const mode = getMeasurementMode(question);
  if (!mode) return null;
  const combined = combinedQuestionText(question);
  const hit = MEASUREMENT_AUTO_PHRASES.find((p) => p.mode === mode && p.test(combined));
  return { mode, phrase: hit ? hit.phrase : null };
}

export function getMeasurementTolerance(question) {
  const fromData = question?.answer_data?.measurement_tolerance;
  return {
    angle: Number(fromData?.angle) || 2,
    length_cm: Number(fromData?.length_cm) || 0.2,
    length_pct: Number(fromData?.length_pct) || 0.05,
  };
}

export function measurementModeForSub(parentQuestion, subQuestion) {
  if (isComprehensionStem(parentQuestion)) {
    return getMeasurementMode(subQuestion);
  }
  const forcedSub = String(
    subQuestion?.answer_data?.force_measurement_tool ||
    subQuestion?.answer_data?.interactive?.force_tool ||
    subQuestion?.answer?.solution_geometry?.interactive?.force_tool ||
    ""
  ).toLowerCase();
  if (forcedSub === "none") return null;
  const subMode = getMeasurementMode(subQuestion);
  const parentMode = getMeasurementMode(parentQuestion);
  // The part's own wording wins. The parent's text is every part's text concatenated, so one
  // "Construct triangle ADC" part made getMeasurementMode(parent) return CONSTRUCT and that used to
  // override every sibling — "Measure the size of obtuse angle ABC" and "Measure the length of BD" both
  // lost their protractor and ruler to a construction board they had no use for. The parent is still
  // the fallback for a part whose own wording names no tool.
  if (subMode) return subMode;
  return parentMode;
}

export function getPxPerCm(...questions) {
  for (const q of questions) {
    const v = Number(q?.answer_data?.geometry_config?.pixels_per_cm);
    if (Number.isFinite(v) && v > 0) return v;
  }
  return undefined;
}

/**
 * `pixels_per_cm` is authored against the image's NATURAL size, but a figure is drawn at whatever size
 * fits the viewport — and, in the Enlarge dialog, at whatever zoom the student picked. The instruments
 * must follow that, so every place that shows a measurable figure reports the scale AS DISPLAYED
 * through this one function. objectFit: contain letterboxes the image inside its element box, so the
 * drawn image is the smaller of the two axis ratios — not the element width.
 */
export function effectivePxPerCm(imgEl, authoredPxPerCm) {
  const natural = Number(authoredPxPerCm);
  if (!imgEl || !Number.isFinite(natural) || natural <= 0) return undefined;
  const { naturalWidth, naturalHeight } = imgEl;
  if (!naturalWidth || !naturalHeight) return undefined;
  const rect = imgEl.getBoundingClientRect();
  if (!rect.width || !rect.height) return undefined;
  const scale = Math.min(rect.width / naturalWidth, rect.height / naturalHeight);
  if (!Number.isFinite(scale) || scale <= 0) return undefined;
  return natural * scale;
}

/**
 * "drawn to a scale of 1 cm : 125 metres" — the conversion the student has to apply after reading the
 * ruler. Surfaced next to the ruler so it is not left three paragraphs up the stem.
 */
export function getScaleDrawingNote(...questions) {
  for (const q of questions) {
    if (!q) continue;
    const text = plainText(q?.question_text || q?.group_title);
    const m =
      text.match(/1\s*cm\s*(?::|=|\bto\b|\brepresents\b)\s*([\d.,]+)\s*(metres|meters|m|kilometres|kilometers|km|cm|miles|mile|feet|ft)\b/i);
    if (m) {
      const unit = m[2].toLowerCase();
      return `1 cm on the ruler = ${m[1]} ${unit}`;
    }
  }
  return null;
}

export function getGeometryConfig(...questions) {
  for (const q of questions) {
    const cfg = q?.answer_data?.geometry_config;
    if (cfg && typeof cfg === "object") return cfg;
  }
  return null;
}

/**
 * Rough-sketch construct items: calibrated Construction Board tab.
 *
 * Called as (part, parent) or (question). The part's OWN setting wins and the parent is consulted only
 * when the part says nothing — the same precedence measurementModeForSub applies to the instruments.
 * It used to veto whenever ANY argument said false, so a parent marked "no Construction Board" silenced
 * a part that asked for one, and a grouped question where only part (i) constructs handed the board to
 * every sibling. An explicit true is honoured on its own: the author ticked the box for this part.
 *
 * Bare geometry_config.kind=construct_measure is not enough — that leftover metadata wrongly opened
 * the board on knowledge stems (Compass directions bearing conversion). Explicit
 * allow_construct_board still wins; otherwise require construct wording / force_tool.
 */
export function shouldShowConstructBoard(...questions) {
  for (const q of questions) {
    const own = q?.answer_data?.allow_construct_board;
    if (typeof own === "boolean") return own;
  }
  for (const q of questions) {
    if (!q) continue;
    if (getMeasurementMode(q) === MEASUREMENT_MODES.CONSTRUCT) return true;
  }
  return false;
}
